import React, {useEffect, useState} from "react";

import {clearCache, withCache} from "../cache";
import {useApi} from "../PlayMakersProvider";
import {AssetExtendedType, AssetPayloadType, AssetType} from "../types/assets.types";
import {usePagination} from "./usePagination";

type AssetPushMethod = (updatedAsset?: Partial<AssetPayloadType & {newFile: File}>) => Promise<AssetType>;
type AssetFetchMethod = (quiet?: boolean) => Promise<AssetType>;
type AssetPushFileMethod = (file: File | null) => Promise<void>;
type AssetDeleteMethod = () => Promise<void>;

interface UseAssetReturn extends Partial<AssetType> {
  asset: AssetType | null;
  fetched: boolean;
  setAsset: React.Dispatch<React.SetStateAction<AssetType | null>>;
  setFile: React.Dispatch<React.SetStateAction<File | null>>;
  push: AssetPushMethod;
  delete: AssetDeleteMethod;
  pushFile: AssetPushFileMethod;
  refresh: AssetFetchMethod;
}

export const useAsset = (id?: string): UseAssetReturn => {
  const {createAsset, getAsset, updateAsset, updateAssetFile, deleteAsset} = useApi();

  let assetId = id;

  const [asset, setAsset] = useState<AssetType | null>(null);
  const [fetched, setFetched] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);

  const fetchAsset: AssetFetchMethod = async(quiet = false) => {
    try {
      if(!id) {
        setAsset(null);
        setFetched(false);
        throw new Error("No asset to fetch");
      }

      if(!quiet) setFetched(false);
      const asset = await withCache(getAsset, [id], 300_000);
      setAsset(asset);
      return asset;
    } catch (error) {
      console.error(`Error fetching asset ${id}`, error);
      throw new Error(`Error fetching asset ${id}`, {cause: error});
    } finally {
      setFetched(true);
    }
  };

  const pushFile: AssetPushFileMethod = async(f = file) => {
    if(!assetId || !f || typeof f === "string") return;
    try {
      await updateAssetFile(assetId, f);
      clearCache("getAsset", [assetId]);
    } catch (error) {
      console.error(`Error updating file for asset '${assetId}'`, error);
      throw new Error(`Error updating file for asset ${id}`, {cause: error});
    }
  };

  const pushAsset: AssetPushMethod = async updatedAsset => {
    const newAsset = {...asset, ...updatedAsset};

    if(!newAsset) throw new Error("No asset");

    const {data, description, name, projectId, schemaId, tags = [], type, url, newFile} = newAsset;

    const params: {[key: string]: unknown} = {};
    if(data !== undefined) params.data = data;
    if(description !== undefined && description)
      params.description = description;
    if(name) params.name = name;
    if(tags !== undefined) params.tags = tags;
    if(url !== undefined) params.url = url;
    if(!id) {
      if(projectId) params.projectId = projectId;
      if(schemaId) params.schemaId = schemaId;
      if(type) params.type = type;
    }

    try {
      const asset = id
        ? await updateAsset(id, params)
        : await createAsset(params);
      if(!asset) throw new Error("Asset not uploaded");
      assetId = asset?.id;
      if(newFile || file) await pushFile(newFile || file);
      clearCache("getAsset", [schemaId]);
      if(!projectId && !schemaId) clearCache("getGlobalAssets");
      if(projectId) clearCache("getProjectAssets");
      clearCache("getSchemaAssets");
      setAsset(asset);
      return asset;
    } catch (error) {
      console.error(`Error pushing asset '${id}'`, error);
      throw new Error(`Error pushing asset '${id}'`, {cause: error});
    }
  };

  const deleteAssetMethod: AssetDeleteMethod = async() => {
    try {
      if(!id) throw new Error("No asset to delete");
      await deleteAsset(id);
    } catch (error) {
      console.error(`Error deleting asset '${id}'`, error);
      throw new Error(`Error deleting asset '${id}'`, {cause: error});
    }
  };

  useEffect(() => {
    fetchAsset();
  }, [id]);

  return {
    ...asset,
    asset,
    delete: deleteAssetMethod,
    fetched,
    push: pushAsset,
    pushFile,
    refresh: fetchAsset,
    setAsset,
    setFile
  };
};

type AssetsFetchMethod = (quiet?: boolean) => Promise<AssetExtendedType[]>;
type AssetsFetchMoreMethod = (refresh?: boolean) => Promise<void>;

interface UseAssetsReturn {
  assets: AssetExtendedType[];
  fetched: boolean;
  hasMore: boolean;
  currentPage: number;
  fetchMore: AssetsFetchMoreMethod;
  refresh: AssetsFetchMethod;
}

export const useAssets = (params: {[key: string]: any} = {}): UseAssetsReturn => {
  const {schemaId, projectId, tags, type, matchTags, page, limit} = params;

  const {getGlobalAssets, getProjectAssets, getSchemaAssets, deleteAsset} = useApi();

  const [assets, setAssets] = useState<AssetExtendedType[]>([]);
  const [fetched, setFetched] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const {appendNextPage, refreshLastPage, checkHasMore, currentPage} =
    usePagination<AssetExtendedType>(setAssets, setHasMore, hasMore, params?.page, params?.limit);

  const extendAssets = (assets: AssetType[]): AssetExtendedType[] => assets.map(asset => ({
    ...asset,
    delete: () => deleteAsset(asset.id)
  }));

  const fetchNewAssets = async(params: {[key:string]: any} = {}): Promise<AssetExtendedType[]>  => {
    let newAssets: AssetType[];
    if(schemaId)
      newAssets = await withCache(getSchemaAssets, [schemaId, {limit, matchTags, page, type, ...params}], 300_000);
    else if(projectId)
      newAssets = await withCache(getProjectAssets, [{limit, page, tags, type, ...params}, projectId], 300_000);
    else
      newAssets = await withCache(getGlobalAssets, [{limit, page, tags, type, ...params}], 600_000);
    return extendAssets(newAssets);
  };

  const fetchAssets: AssetsFetchMethod = async(quiet = false) => {
    try {
      if(!quiet) setFetched(false);
      const assets: AssetExtendedType[] = checkHasMore(await fetchNewAssets());
      setAssets(assets);
      return assets;
    } catch (error) {
      console.error("Error fetching assets", error);
      throw new Error("Error fetching assets", {cause: error});
    } finally {
      setFetched(true);
    }
  };

  const fetchMore:AssetsFetchMoreMethod = async(refresh = false) => {
    try {
      setFetched(false);

      if(refresh) {
        if(schemaId) {
          await refreshLastPage(async appendParams =>
            extendAssets(await getSchemaAssets(schemaId, {matchTags, type, ...appendParams})));
        } else if(projectId) {
          await refreshLastPage(async appendParams =>
            extendAssets(await getProjectAssets({tags, type, ...appendParams}, projectId)));
        } else {
          await refreshLastPage(async appendParams =>
            extendAssets(await getGlobalAssets({tags, type, ...appendParams})));
        }
      } else
        appendNextPage(fetchNewAssets);

    } catch (error) {
      console.error("Error fetching assets", error);
      throw new Error("Error fetching assets", {cause: error});
    } finally {
      setFetched(true);
    }
  };

  useEffect(() => {
    fetchAssets();
  }, [schemaId, projectId, tags, type, matchTags, page]);

  return {
    assets,
    currentPage,
    fetchMore,
    fetched,
    hasMore,
    refresh: fetchAssets
  };
};

export const useAssetsByProject = (projectId: string, params: any={}) => useAssets({...params, projectId});
export const useAssetsBySchema = (schemaId: string, params: any={}) => useAssets({...params, schemaId});
export const useAssetsByTags = (tags:Array<string>, params: any={}) => useAssets({...params, tags});
export const useTutorials = (params:any ={}) => useAssets({...params, type: "tutorial"});
export const useTutorialsByProject = (projectId?:string, params:any={}) => useTutorials({...params, projectId});
export const useTutorialsBySchema = (schemaId:string, params:any={}) => useTutorials({...params, schemaId});
export const useTutorialsByTags = (tags:Array<string>, params:any={}) => useTutorials({...params, tags});
